import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import Breadcrumb from "@/components/base/Breadcrumb";

const services = [
  {
    icon: "ri-movie-2-line",
    title: "多类别影视聚合",
    desc: "涵盖电影、电视剧、综艺、动漫四大类内容，用统一的信息结构整理，方便快速浏览与比较。",
  },
  {
    icon: "ri-filter-3-line",
    title: "精准筛选与排序",
    desc: "支持地区、年份、类型多条件组合筛选，并可按热门、最新、评分排序，几秒定位想看的内容。",
  },
  {
    icon: "ri-search-line",
    title: "全站关键词搜索",
    desc: "输入片名、主演、导演或类型即可模糊匹配，搜索结果支持按内容类别再次筛选。",
  },
  {
    icon: "ri-bookmark-line",
    title: "本地收藏演示",
    desc: "收藏状态保存在浏览器本地，无需登录即可记录喜欢的内容，作为前端交互演示。",
  },
];

const steps = [
  { title: "从顶部导航进入分类", desc: "点击电影、电视剧、综艺或动漫，进入对应分类页浏览。" },
  { title: "用筛选栏缩小范围", desc: "按地区、年份、类型勾选条件，选择排序方式，结果会即时刷新。" },
  { title: "打开详情了解作品", desc: "点击任意卡片进入详情页，查看元信息、剧情简介与相关推荐。" },
  { title: "收藏或分享喜欢的作品", desc: "在详情页点击收藏保存到本地，或复制链接分享给朋友。" },
];

export default function AboutPage() {
  useSEO(
    "关于我们 - 色虎视频",
    "了解色虎视频的平台定位与服务特色：高清影视资源聚合、多维筛选、关键词搜索与相关推荐，帮助你高效发现想看的影视作品。",
  );

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      <section className="overflow-hidden rounded-lg bg-primary-700 px-6 py-10 md:px-10 md:py-14">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 font-label text-xs font-semibold text-background-50">
          <i className="ri-information-line" aria-hidden="true" />
          关于色虎视频
        </span>
        <h1 className="mt-4 max-w-3xl font-heading text-3xl font-extrabold tracking-tight text-background-50 md:text-4xl">
          高清影视资源聚合平台，让发现好片更简单
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-100/90 md:text-base">
          色虎视频面向广大影视爱好者，专注于把海量影视资源以清晰、专业的方式组织起来。无论是追最新上线的电影、热播剧集，还是想找一部综艺放松、一部动漫下饭，都能在这里快速定位并了解作品详情。
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/category/movie"
            className="inline-flex h-11 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
          >
            <i className="ri-compass-3-line text-lg" aria-hidden="true" />
            开始浏览
          </Link>
          <Link
            to="/search"
            className="inline-flex h-11 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
          >
            <i className="ri-search-line text-lg" aria-hidden="true" />
            搜索影视
          </Link>
        </div>
      </section>

      <section className="mt-10 md:mt-12">
        <h2 className="font-heading text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">
          平台定位与服务
        </h2>
        <p className="mt-2 max-w-3xl text-sm text-foreground-500 md:text-base">
          我们围绕“发现、筛选、了解”三个环节设计服务，帮助你更高效地找到心仪的影视作品。
        </p>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((item) => (
            <div
              key={item.title}
              className="rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                <i className={`${item.icon} text-2xl`} aria-hidden="true" />
              </span>
              <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">
                {item.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-500">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 md:mt-12">
        <h2 className="font-heading text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">
          使用说明
        </h2>
        <p className="mt-2 max-w-3xl text-sm text-foreground-500 md:text-base">
          只需四步，即可高效浏览和使用站内资源。
        </p>
        <ol className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          {steps.map((step, index) => (
            <li
              key={step.title}
              className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-500 font-label text-sm font-bold text-background-50">
                {index + 1}
              </span>
              <div>
                <h3 className="font-heading text-base font-bold text-foreground-950">{step.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-500">{step.desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-10 md:mt-12">
        <h2 className="font-heading text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">
          联系我们
        </h2>
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-600">
              <i className="ri-mail-line text-2xl" aria-hidden="true" />
            </span>
            <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">邮箱联系</h3>
            <p className="mt-1.5 text-sm text-foreground-500">support@sehu-video.com</p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-600">
              <i className="ri-customer-service-2-line text-2xl" aria-hidden="true" />
            </span>
            <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">服务时间</h3>
            <p className="mt-1.5 text-sm text-foreground-500">工作日 9:00 - 18:00</p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-600">
              <i className="ri-share-line text-2xl" aria-hidden="true" />
            </span>
            <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">社交媒体</h3>
            <div className="mt-3 flex items-center gap-2">
              {["ri-wechat-fill", "ri-weibo-fill", "ri-twitter-x-line", "ri-youtube-line"].map((icon) => (
                <span
                  key={icon}
                  className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-700"
                >
                  <i className={icon} aria-hidden="true" />
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}